import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { customFetcher } from './customFetcher';
import { ModuleKey, ModuleSettings } from '../utils/moduleEnabled';

export const MODULE_SETTINGS_QUERY_KEY = ['module-settings'] as const;

const moduleSettingsSchema = z.object({
    hutbook: z.boolean(),
    meetings: z.boolean(),
    blog: z.boolean(),
    storage: z.boolean(),
    info: z.boolean(),
});

const parseModuleSettings = (payload: unknown): ModuleSettings => moduleSettingsSchema.parse(payload);

export const useModuleSettings = (options?: { enabled?: boolean }) => {
    const enabled = options?.enabled ?? true;
    const { data, isLoading, isError, refetch } = useQuery<ModuleSettings, Error>({
        queryKey: MODULE_SETTINGS_QUERY_KEY,
        queryFn: async () => parseModuleSettings(await customFetcher<unknown>({ endpoint: '/organization/modules/' })),
        staleTime: 5 * 60 * 1000,
        enabled,
    });
    // Solange die Query (noch) deaktiviert ist, gilt sie als "lädt" → fail-closed.
    return { modules: data, isLoading: isLoading || !enabled, isError, refetch };
};

export const useUpdateModuleSettings = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: updateModules, isPending: isUpdatingModules } = useMutation<
        ModuleSettings,
        Error,
        Partial<Record<ModuleKey, boolean>>
    >({
        mutationFn: async (changes) =>
            parseModuleSettings(
                await customFetcher<unknown>({
                    endpoint: '/organization/modules/',
                    method: 'PATCH',
                    body: changes,
                }),
            ),
        onSuccess: (data) => {
            queryClient.setQueryData(MODULE_SETTINGS_QUERY_KEY, data);
            void queryClient.invalidateQueries({ queryKey: MODULE_SETTINGS_QUERY_KEY });
        },
    });
    return { updateModules, isUpdatingModules };
};
